import type React from "react"
import { useEffect, useState } from "react"
import "./ActionSheet.css"

export interface ActionSheetItem {
  label: string
  onClick: () => void
  icon?: React.ReactNode
  variant?: "default" | "danger" | "primary"
  className?: string
}

export interface ActionSheetProps {
  isOpen: boolean
  onClose: () => void
  title?: string
  description?: string
  items: ActionSheetItem[]
}

export const ActionSheet: React.FC<ActionSheetProps> = ({
  isOpen,
  onClose,
  title,
  description,
  items,
}) => {
  const [isAnimating, setIsAnimating] = useState(false)

  useEffect(() => {
    if (isOpen) {
      setIsAnimating(true)
      document.body.style.overflow = "hidden"
    } else {
      const timer = setTimeout(() => setIsAnimating(false), 300)
      document.body.style.overflow = "unset"
      return () => clearTimeout(timer)
    }
  }, [isOpen])

  if (!isOpen && !isAnimating) return null

  return (
    <div 
      className={`action-sheet-backdrop ${isOpen ? "active" : ""}`} 
      onClick={onClose}
    >
      <div 
        className={`action-sheet-container ${isOpen ? "active" : ""}`}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="action-sheet-handle" />
        {(title || description) && (
          <div className="action-sheet-header">
            {title && <h3 className="action-sheet-title">{title}</h3>}
            {description && <p className="action-sheet-description">{description}</p>}
          </div>
        )}
        <div className="action-sheet-content">
          {items.map((item, index) => (
            <button
              key={index}
              className={`action-sheet-item variant-${item.variant || "default"} ${item.className || ""}`}
              onClick={() => {
                item.onClick()
                onClose()
              }}
            >
              {item.icon && <span className="action-sheet-item-icon">{item.icon}</span>}
              <span className="action-sheet-item-label">{item.label}</span>
            </button>
          ))}
        </div>
        <div className="action-sheet-footer">
          <button className="action-sheet-cancel" onClick={onClose}>
            취소
          </button>
        </div>
      </div>
    </div>
  )
}
